import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTerritories } from '../context/TerritoriesContext';
import { useConfig } from '../context/ConfigContext';
import { createLead } from '../data/commands/leadCommands';
import { useAssignableAgents } from '../hooks/useAssignableAgents';
import { isLat, isLng, parseLatLngPair } from '../utils/coords';
import Icon from './Icon';

// New lead modal → createLead (rules enforce territory must be in the actor's scope).
// Assignment (CRM revisions Task 1): a manager/owner picks the Agent + Territory;
// an agent's lead auto-assigns to that agent (the encoder) with Territory set.
// Territories are tenant-aware (read from the active tenant's Firestore docs).
export default function NewLeadModal({ onClose, onCreated }) {
  const {
    tenantId, storeIds = [], role, profile, authUser,
  } = useAuth();
  const { territories: scoped } = useTerritories();
  const { config } = useConfig();
  const channels = config.channels ?? [];
  const tiers = config.tiers ?? [];
  const agents = useAssignableAgents();
  const isAgent = role === 'agent';
  const [form, setForm] = useState({
    businessName: '', category: channels[0] ?? '', tier: tiers[1] ?? tiers[0] ?? '', territoryId: scoped[0]?.id ?? '',
    contactPerson: '', phone: '', leadValue: '', leadScore: '', nextDate: '', ownerUid: '',
    coords: '',
  });
  // Default the territory once the tenant's territories load (async).
  useEffect(() => {
    if (!form.territoryId && scoped.length) setForm((f) => ({ ...f, territoryId: scoped[0].id }));
  }, [scoped, form.territoryId]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const actor = { uid: authUser?.uid, name: profile?.name ?? profile?.email, role };

  async function save() {
    if (!form.businessName.trim()) { setError('Business name is required.'); return; }
    if (!form.territoryId) { setError('Pick a territory.'); return; }
    // Coordinates are optional. If filled, must be a valid "lat, lng" pair.
    let lat = null;
    let lng = null;
    if (form.coords.trim()) {
      const parsed = parseLatLngPair(form.coords);
      if (!parsed || !isLat(parsed.lat) || !isLng(parsed.lng)) {
        setError('Enter coordinates as "lat, lng" (copy-paste from Google Maps), or leave blank.');
        return;
      }
      lat = parsed.lat;
      lng = parsed.lng;
    }
    setSaving(true); setError('');
    // Agents auto-assign to themselves; managers/owners use the picked Agent
    // (when none picked, createLead falls back to the creating actor).
    const picked = isAgent
      ? { ownerUid: authUser?.uid, ownerName: actor.name }
      : (form.ownerUid
        ? { ownerUid: form.ownerUid, ownerName: agents.find((a) => a.uid === form.ownerUid)?.name ?? null }
        : {});
    try {
      await createLead(
        { ...form, lat, lng, ...picked },
        { tenantId, storeId: storeIds[0] ?? null, territories: [] },
        actor,
      );
      onCreated?.();
      onClose();
    } catch (e) {
      setError(e.message || 'Could not create the lead.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-scrim" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <header><Icon name="plus" size={18} /> New lead</header>
        <div className="body">
          <div className="field"><label>Business name</label><input className="input" value={form.businessName} onChange={set('businessName')} placeholder="e.g. The Peninsula Manila" /></div>
          <div className="grid2">
            <div className="field"><label>Channel</label><select className="input" value={form.category} onChange={set('category')}>{channels.map((c) => <option key={c}>{c}</option>)}</select></div>
            <div className="field"><label>Tier</label><select className="input" value={form.tier} onChange={set('tier')}>{tiers.map((t) => <option key={t}>{t}</option>)}</select></div>
            <div className="field"><label>Territory</label>
              <select className="input" value={form.territoryId} onChange={set('territoryId')}>
                {scoped.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </div>
            {isAgent ? (
              <div className="field"><label>Assigned agent</label>
                <input className="input" value={actor.name ?? 'You'} disabled title="Leads you encode are assigned to you" />
              </div>
            ) : (
              <div className="field"><label>Assigned agent</label>
                <select className="input" value={form.ownerUid} onChange={set('ownerUid')}>
                  <option value="">— unassigned —</option>
                  {agents.map((a) => <option key={a.uid} value={a.uid}>{a.name}</option>)}
                </select>
              </div>
            )}
            <div className="field"><label>Lead value amount (₱/mo)</label><input className="input" type="number" min="0" value={form.leadValue} onChange={set('leadValue')} placeholder="e.g. 25000" /></div>
            <div className="field"><label>Contact person</label><input className="input" value={form.contactPerson} onChange={set('contactPerson')} /></div>
            <div className="field"><label>Phone / Viber</label><input className="input" value={form.phone} onChange={set('phone')} /></div>
            <div className="field"><label>Lead score</label><input className="input" value={form.leadScore} onChange={set('leadScore')} /></div>
          </div>
          <div className="field"><label>Coordinates <span className="muted" style={{ fontSize: 'var(--text-caption)' }}>· optional · paste from Google Maps</span></label><input className="input" value={form.coords} onChange={set('coords')} placeholder="e.g. 14.5547, 121.0244" /></div>
          <div className="field"><label>Next visit date</label><input className="input" type="date" value={form.nextDate} onChange={set('nextDate')} /></div>
          {error && <div className="form-error">{error}</div>}
        </div>
        <footer>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={saving} onClick={save}>{saving ? 'Creating…' : 'Create lead'}</button>
        </footer>
      </div>
    </div>
  );
}
